import { brandApi } from "@/lib/axios";
import type { DraftAdConfig } from "./contentProgram.service";

export interface MetaCampaignProgram {
  id: string;
  brandDetailId: string;
  title: string;
  commissionRate: number;
  status: string;
  createdAt: string;
  updatedAt: string;
  description?: string | null;
  isOpenToAllEnrolledAffiliates?: boolean;
}

export interface MetaCampaignContent {
  id: string;
  metaAdsCampaignProgramId: string;
  brandDetailId: string;
  creatorAffiliateId: string | null;
  title: string | null;
  mediaType: string | null;
  mediaUrl: string | null;
  thumbnailUrl: string | null;
  destinationUrl: string | null;
  status: string;
  reviewMessage?: string | null;
  createdAt: string;
  updatedAt: string;
  /** A DRAFT row was prepared by a TikTok ad request; its adConfig seeds the launch form. */
  launchedAd?: { id: string; status: string; adConfig: DraftAdConfig | null } | null;
}

export interface MetaCampaignLaunchedAd {
  id: string;
  metaAdsCampaignProgramId: string;
  metaAdsCampaignContentId: string;
  creatorAffiliateId: string | null;
  metaCampaignId: string | null;
  metaAdsetId: string | null;
  metaAdId: string | null;
  metaCreativeId: string | null;
  launchError: string | null;
  createdAt: string;
  updatedAt: string;
  metaAdsCampaignContent?: {
    id: string;
    title: string | null;
    destinationUrl: string | null;
    mediaType: string | null;
    thumbnailUrl: string | null;
  };
}

export async function createMetaCampaignProgram(body: {
  title: string;
  commissionRate: number;
  description?: string;
  isOpenToAllEnrolledAffiliates?: boolean;
  allowedAffiliateIds?: string[];
}): Promise<MetaCampaignProgram> {
  const { data } = await brandApi.post<MetaCampaignProgram>(
    "/meta-ads/campaign-programs",
    body,
  );
  return data;
}

export async function listMetaCampaignPrograms(): Promise<MetaCampaignProgram[]> {
  const { data } = await brandApi.get<MetaCampaignProgram[]>(
    "/meta-ads/campaign-programs",
  );
  return data;
}

export async function listMetaCampaignContent(
  programId: string,
): Promise<MetaCampaignContent[]> {
  const { data } = await brandApi.get<MetaCampaignContent[]>(
    `/meta-ads/campaign-programs/${programId}/content`,
  );
  return data;
}

export async function listMetaCampaignAds(
  programId: string,
): Promise<MetaCampaignLaunchedAd[]> {
  const { data } = await brandApi.get<MetaCampaignLaunchedAd[]>(
    `/meta-ads/campaign-programs/${programId}/ads`,
  );
  return data;
}

export async function launchMetaCampaignFromContent(
  programId: string,
  body: {
    contentId: string;
    adConfig: {
      facebookPageId: string;
      campaignName?: string;
      /** TRAFFIC optimises for link clicks; SALES for purchases and needs a pixel. */
      objective?: "OUTCOME_TRAFFIC" | "OUTCOME_SALES";
      dailyBudget?: number;
      countries?: string[];
      ageMin?: number;
      ageMax?: number;
      /** Run inside an existing campaign / ad set instead of creating them. */
      targetCampaignId?: string | null;
      targetAdsetId?: string | null;
      actOverride?: string;
    };
  },
): Promise<{
  metaCampaignId: string;
  metaAdsetId: string;
  metaCreativeId: string;
  metaAdId: string;
  urlTags: string;
}> {
  const { data } = await brandApi.post(
    `/meta-ads/campaign-programs/${programId}/launch`,
    body,
  );
  return data as {
    metaCampaignId: string;
    metaAdsetId: string;
    metaCreativeId: string;
    metaAdId: string;
    urlTags: string;
  };
}
